"use client";

import React, { useEffect, useRef } from "react";
import { Renderer, Program, Mesh, Triangle } from "ogl";

const vertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`;

const createShader = (
  baseColor: [number, number, number],
  accentColor: [number, number, number],
  intensity: number,
  mixStrength: number
) => `
  precision highp float;

  uniform float uTime;
  uniform vec3 uResolution;
  varying vec2 vUv;

  void main() {
    float mr = min(uResolution.x, uResolution.y);
    vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

    float d = -uTime * 1.2;
    float a = 0.0;
    
    for (float i = 0.0; i < 8.0; ++i) {
      a += cos(i - d - a * uv.x);
      d += sin(uv.y * i + a);
    }
    
    d += uTime * 1.0;
    vec3 col = vec3(${baseColor.join(", ")}) + 
               ${intensity} * vec3(sin(d * 0.5) + 0.5, cos(a * 0.8) * 0.2, sin(a * 0.3 + d * 0.2) + 0.7);
    col = mix(col, vec3(${accentColor.join(", ")}), ${mixStrength});

    gl_FragColor = vec4(col, 1.0);
  }
`;

const Novatrix: React.FC<{
  baseColor: [number, number, number];
  accentColor: [number, number, number];
  intensity: number;
  mixStrength: number;
}> = ({ baseColor, accentColor, intensity, mixStrength }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      antialias: true,
      alpha: true,
    });
    const gl = renderer.gl;

    const handleResize = () => {
      const { width, height } = container.getBoundingClientRect();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    const geometry = new Triangle(gl);

    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: createShader(baseColor, accentColor, intensity, mixStrength),
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: [
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height,
          ],
        },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });

    let rafId: number;

    const animate = (time: number) => {
      rafId = requestAnimationFrame(animate);
      program.uniforms.uTime.value = time * 0.001;
      renderer.render({ scene: mesh });
    };

    container.appendChild(gl.canvas);
    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      if (container.contains(gl.canvas)) {
        container.removeChild(gl.canvas);
      }
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      geometry.remove();
      program.remove();
    };
  }, [baseColor, accentColor, intensity, mixStrength]);

  return <div ref={containerRef} className="h-full w-full bg-transparent" />;
};

interface BackgroundProps {
  baseColor: [number, number, number];
  accentColor: [number, number, number];
  intensity: number;
  mixStrength: number;
}

const Background: React.FC<BackgroundProps> = ({
  baseColor,
  accentColor,
  intensity,
  mixStrength,
}) => {
  return (
    <div className="absolute inset-0 h-full w-full">
      <Novatrix
        baseColor={baseColor}
        accentColor={accentColor}
        intensity={intensity}
        mixStrength={mixStrength}
      />
    </div>
  );
};

export default Background;
